<script setup>
import { ChevronRight } from 'lucide-vue'
import BackHeader from './BackHeader.vue'
import Card from './Card.vue'
import { computed, ref, watch } from 'vue'

const props = defineProps({
  visitors: {
    required: true,
    type: Array,
  },
  simplified: {
    required: false,
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['details', 'add-invitation'])

const selectedVisitors = ref([])
const selectAllCheckbox = ref(null)
const visitorSearch = ref('')

const filteredVisitors = computed(() => {
  try {
    const re = new RegExp(visitorSearch.value, 'i')
    return props.visitors
      .filter(({ name, email }) => re.test(name) || re.test(email))
      .sort((a, b) => selectedVisitors.value.includes(b.id) - selectedVisitors.value.includes(a.id))
  } catch {
    return []
  }
})
const isAllSelected = computed(() => {
  return filteredVisitors.value.every(v => selectedVisitors.value.includes(v.id))
})

const isIndeterminate = computed(() => {
  return filteredVisitors.value.some(v => selectedVisitors.value.includes(v.id)) && !isAllSelected.value
})

const hasSelectedNotShown = computed(() => {
  return selectedVisitors.value.some(v => !filteredVisitors.value.some(fv => fv.id === v))
})

watch([isIndeterminate, selectAllCheckbox], ([indeterminate, checkbox]) => {
  if (checkbox) {
    checkbox.indeterminate = indeterminate
  }
}, { immediate: true })

function addInvitation (visitorIds) {
  emit('add-invitation', visitorIds)
}

function onCheckBoxClick (event) {
  if (event.target.checked) {
    const newSelected = filteredVisitors.value.map(v => v.id).filter(id => !selectedVisitors.value.includes(id))
    selectedVisitors.value.push(...newSelected)
  } else {
    selectedVisitors.value = selectedVisitors.value.filter(id => !filteredVisitors.value.some(v => v.id === id))
  }
}
</script>

<template>
  <div>
    <BackHeader
      v-if="!simplified"
      @click="$emit('close')"
    />
    <h2
      v-if="!simplified"
      class="text-base font-medium my-4"
    >
      {{ $t('Visitors.InviteAttendees') }}
    </h2>
    <input
      v-model="visitorSearch"
      type="search"
      class="input w-full mb-3 shrink-0"
      :placeholder="$t('Visitors.FilterVisitors')"
    >
    <div class="flex item-center justify-between gap-3">
      <label class="flex items-center cursor-pointer mt-2 group mb-3">
        <input
          ref="selectAllCheckbox"
          :checked="isAllSelected"
          type="checkbox"
          class="checkbox"
          @change="onCheckBoxClick"
        >
        <span class="input-label transition group-hover:text-neutral-gray-700">{{ $t('Visitors.SelectAll') }}</span>
      </label>
      <button
        v-if="simplified"
        class="btn btn-primary btn-small group"
        :disabled="!selectedVisitors.length"
        @click="addInvitation(selectedVisitors)"
      >
        {{ $tc('Visitors.InviteNVisitors', selectedVisitors.length) }}
        <ChevronRight class="w-0 h-5 group-hover:w-5 transition-all" />
      </button>
    </div>
    <TransitionGroup
      tag="div"
      name="transition-list"
      class="flex flex-col gap-3 mb-3 relative grow overflow-y-auto -m-1 p-1 overflow-x-hidden"
    >
      <Card
        v-for="visitor in filteredVisitors"
        :key="visitor.id"
        :selected="selectedVisitors.includes(visitor.id)"
        :subtitle="visitor.email"
        class="shrink-0"
        @open="emit('details', visitor)"
      >
        <template #action>
          <input
            type="checkbox"
            class="checkbox shrink-0 m-1"
            :checked="selectedVisitors.includes(visitor.id)"
            @click.stop
            @change="$event.target.checked ? selectedVisitors.push(visitor.id) : selectedVisitors.splice(selectedVisitors.indexOf(visitor.id), 1)"
          >
        </template>
        <template #title>
          {{ visitor.name }}
          <span
            v-if="!visitor.verified"
            v-tippy="$t('Visitors.Unverified')"
            class="inline-block text-white px-1.5 text-xs rounded font-medium text-center bg-amber-500 cursor-help"
          >?</span>
        </template>
      </Card>
    </TransitionGroup>
    <template v-if="!simplified">
      <div class="divider mb-3" />
      <template v-if="hasSelectedNotShown">
        <div class="text-sm text-neutral-gray-500 mb-3">
          {{ $t('Visitors.SomeSelectedNotShown') }}
        </div>
      </template>
      <button
        class="btn btn-primary w-full"
        :disabled="!selectedVisitors.length"
        @click="addInvitation(selectedVisitors)"
      >
        {{ $tc('Visitors.InviteNVisitors', selectedVisitors.length) }}
      </button>
    </template>
  </div>
</template>
